import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NbTabComponent } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { SubscribedComponent } from '../../subscribed.component';
import { DualAxisDelegate, DualAxisEvent } from '../../dual-axis/dual-axis.component';
import { ColDef } from '../../interfaces/colDef';
import { TreeNode } from '../../interfaces/treenode';
import { Paged } from '../../interfaces/paged';
import { TableOptions } from '../../settings/table-options/table-options.component';
import { INTAKE_FILTERS, IntakeFilter, IntakeMailListItem, IntakeMailListResponse, intakePaged } from '../../interfaces/intake';
import { IntakeService, intakeContentGone } from '../../shared/intake.service';
import { intakeColumns, intakeStickyColumns } from '../coldefs-intake';

export interface IntakeTab {
  filter: IntakeFilter;
  label: string;
  title: string;
}

/** The URL's ?filter= when it is one of the five tabs, else null (the caller rewrites the URL). */
export function normalizeFilter(raw: unknown): IntakeFilter | null {
  return typeof raw === 'string' && (INTAKE_FILTERS as readonly string[]).includes(raw) ? raw as IntakeFilter : null;
}

/** Smallest per_page D1 accepts (25, 50, 100) that is >= n; 100 above that, 25 for junk. */
export function intakePerPage(n: number): 25 | 50 | 100 {
  return !(n > 0) ? 25 : n <= 25 ? 25 : n <= 50 ? 50 : 100;
}

/**
 * nb-badge status per verdict, the badge Market detail uses for source and state. The accent marks
 * a Direct request, warning a mail that waits on someone, info an open update; the rest stay basic.
 */
// Colour only where someone acts on the request. Primary is the one badge fill with AA white text;
// info (#0095ff) and warning (#ffaa00) fail it. Held is most of Needs action, so it stays neutral.
export function intakeVerdictStatus(row: Pick<IntakeMailListItem, 'verdict' | 'vacancy' | 'update_state'>): string {
  switch (row.verdict) {
    case 'direct_request': return 'primary';
    case 'update_candidate': return row.update_state ? 'basic' : 'primary';
    default: return 'basic';
  }
}

/** Chip label: the verdict, or the more specific state a user acts on. */
export function intakeVerdictKey(row: Pick<IntakeMailListItem, 'verdict' | 'vacancy' | 'update_state'>): string {
  if (row.verdict === 'direct_request' && row.vacancy?.needs_details) { return 'intake.list.needs_details'; }
  if (row.verdict === 'update_candidate' && row.update_state) { return `intake.update_state.${row.update_state}`; }
  return `intake.verdict.${row.verdict}`;
}

@Component({
  selector: 'bst-intake-list',
  templateUrl: './intake-list.component.html',
  styleUrls: ['./intake-list.component.scss'],
  standalone: false
})
export class IntakeListComponent extends SubscribedComponent implements OnInit, DualAxisDelegate<IntakeMailListItem> {

  @ViewChild('cellTemplate', { static: true, read: TemplateRef }) cellTemplateOutlet: TemplateRef<any>;
  @ViewChild('cardTemplate', { static: true, read: TemplateRef }) cardTemplateOutlet: TemplateRef<any>;

  data: TreeNode<IntakeMailListItem>[] = [];
  stickyColumnsLeft: ColDef[] = intakeStickyColumns;
  standardColumns: ColDef[] = intakeColumns;
  stickyColumnsRight: ColDef[] = [];
  options: TableOptions = {
    display: [...intakeStickyColumns, ...intakeColumns].map(c => ({ col: c.name, shown: true })),
    others: {},
    filter: {},
    sorts: []
  };
  events = new Subject<DualAxisEvent>();
  readonly DualAxisEvent = DualAxisEvent;

  filter: IntakeFilter = 'needs_action';
  searchQuery = '';
  tabs: IntakeTab[] = [];

  private appliedQuery = '';
  private counts: IntakeMailListResponse['counts'] = null;
  private searchSubject = new Subject<string>();
  private pagedGeneration = 0;

  constructor(private intake: IntakeService,
              private route: ActivatedRoute,
              private router: Router,
              private translate: TranslateService) {
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.tabs = INTAKE_FILTERS.map(filter => ({ filter, label: '', title: '' }));
    // stream, not instant: on a hard reload the language file is not loaded yet at ngOnInit.
    this.subs.push(this.translate.stream(INTAKE_FILTERS.map(f => `intake.list.tab.${f}`)).subscribe(labels => {
      this.tabs.forEach(tab => tab.label = labels[`intake.list.tab.${tab.filter}`]);
      this.applyCounts(this.counts);
    }));
    this.subs.push(this.route.queryParamMap.subscribe(params => {
      const filter = normalizeFilter(params.get('filter'));
      if (!filter) {
        void this.router.navigate([], { relativeTo: this.route, queryParams: { filter: 'needs_action' }, replaceUrl: true });
        return;
      }
      if (filter !== this.filter) {
        this.filter = filter;
        this.events.next(DualAxisEvent.RESET);
      }
    }));
    this.subs.push(this.searchSubject.pipe(debounceTime(500)).subscribe(query => {
      this.appliedQuery = (query ?? '').trim();
      this.events.next(DualAxisEvent.RESET);
    }));
  }

  /** Read once at activation; the object form makes TitleService wait for the translations. */
  get headerTitle(): { title: string; translate: boolean } {
    return { title: 'intake.title', translate: true };
  }

  trackRow = (node: TreeNode<IntakeMailListItem>): any => node.data?.id ?? node;

  rowClass = (row: IntakeMailListItem): string | null => intakeContentGone(row) ? 'content-gone' : null;

  onTab(tab: NbTabComponent): void {
    const filter = normalizeFilter(tab?.tabId);
    if (!filter || filter === this.filter) { return; }
    void this.router.navigate([], { relativeTo: this.route, queryParams: { filter } });
  }

  onSearchChange(query: string): void {
    this.searchSubject.next(query);
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.searchSubject.next('');
  }

  async paged(page: number, pageSize: number): Promise<Paged<IntakeMailListItem>> {
    const generation = ++this.pagedGeneration;
    this.loading.next(true);
    try {
      const res: IntakeMailListResponse = await this.intake.list(this.filter, this.appliedQuery, page, intakePerPage(pageSize)).catch(() => null);
      if (!res) { return this.emptyPage(page); }
      const paged = intakePaged(res);
      // A newer call (tab or search change) owns this.data; dual-axis guards its own state.
      if (generation === this.pagedGeneration) {
        this.data = paged.data.map(row => ({ data: row, children: [] }));
        this.applyCounts(res.counts);
      }
      return paged;
    } finally {
      if (generation === this.pagedGeneration) {
        this.loading.next(false);
      }
    }
  }

  verdictStatus = intakeVerdictStatus;
  verdictKey = intakeVerdictKey;
  contentGone = intakeContentGone;

  /** "Reason / result" key, computed here (D1 sends no reason): the held reason; else the template shows the vacancy title. */
  reasonKey(row: IntakeMailListItem): string | null {
    if (row.verdict === 'held' && row.held_reason) { return `intake.held.${row.held_reason}`; }
    return null;
  }

  get noMailYet(): boolean {
    return this.counts?.all === 0;
  }

  private applyCounts(counts: IntakeMailListResponse['counts']): void {
    this.counts = counts;
    for (const tab of this.tabs) {
      const count = counts?.[tab.filter];
      tab.title = typeof count === 'number' ? `${tab.label} (${count})` : tab.label;
    }
  }

  private emptyPage(page: number): Paged<IntakeMailListItem> {
    return { current_page: page, data: [], from: 0, last_page: 1, per_page: 0, to: 0, total: 0 };
  }
}
